import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useParams,
  type RouteObject,
} from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppShell } from '../shell/AppShell';
import { SaaSShell } from '../shell/SaaSShell';
import { EmptyPage } from '../shell/EmptyPage';
import { PermissionGate } from '../auth/PermissionGate';
import { RoleGate } from '../auth/RoleGate';
import { RequireAuth } from '../auth/RequireAuth';
import { apiFetch } from '../api/client';
import { useAuth } from '../auth/useAuth';
import { useOnboardingStatus } from '../api/onboarding';
import { normalizeAdminWebBasePath } from './adminWebBasePath';
import { SaasEntryGate } from '../pages/SaaS/SaasEntryGate';
import type { FeatureFlags } from '@tokenforge/shared';

// ── Lazy-loaded page modules ────────────────────────────────────────────────
const DashboardPage = lazy(() => import('../pages/Dashboard/Index'));
const ConversationsListPage = lazy(() => import('../pages/Conversations/Index'));
const ConversationDetailPage = lazy(() => import('../pages/Conversations/Detail'));
const WikiListPage = lazy(() => import('../pages/Conversations/WikiList'));
const WikiArticlePage = lazy(() => import('../pages/Conversations/WikiArticle'));
const RAGSavingsPage = lazy(() => import('../pages/Conversations/RAGSavings'));
const ClassifyPage = lazy(() => import('../pages/Conversations/Classify'));
const GraphPage = lazy(() => import('../pages/Conversations/Graph'));
const UsageAttributionPage = lazy(() => import('../pages/Usage/Attribution'));
const ByokAllocatedCostsPage = lazy(() => import('../pages/Usage/ByokAllocatedCosts'));
const CredentialsListPage = lazy(() => import('../pages/Credentials/Index'));
const CredentialNewPage = lazy(() => import('../pages/Credentials/New'));
const CredentialEditPage = lazy(() => import('../pages/Credentials/Edit'));
const RateConfigPage = lazy(() => import('../pages/Credentials/RateConfig'));
const ByokShowbackConfigPage = lazy(() => import('../pages/Credentials/ByokShowbackConfig'));
const RoutingPage = lazy(() => import('../pages/Routing/Index'));
const UsagePage = lazy(() => import('../pages/Usage/Index'));
const WalletsPage = lazy(() => import('../pages/Wallets/Index'));
const ModelInventoryPage = lazy(() => import('../pages/ModelInventory/Index'));
const WalletDetailPage = lazy(() => import('../pages/Wallets/Detail'));
const OrgMembersPage = lazy(() => import('../pages/Org/Members'));
const SecurityRulesPage = lazy(() => import('../pages/Security/Rules'));
const SecurityAuditPage = lazy(() => import('../pages/Security/Audit'));
const SSOConfigPage = lazy(() => import('../pages/Org/SSOConfig'));
const DirectoryIntegrationPage = lazy(() => import('../pages/Integrations/Directory'));
const OrgDepartmentsPage = lazy(() => import('../pages/Org/Departments'));
const ApiKeysPage = lazy(() => import('../pages/Settings/ApiKeys'));
const DlpRulesPage = lazy(() => import('../pages/Settings/DlpRules'));
const OidcProvidersPage = lazy(() => import('../pages/Settings/OidcProviders'));
const OrgSettingsPage = lazy(() => import('../pages/Settings/Org'));
const AboutPage = lazy(() => import('../pages/Settings/About'));
const SLMAuditPage = lazy(() => import('../pages/MaxStubs/SLMAudit'));
const PIIRealtimePage = lazy(() => import('../pages/MaxStubs/PIIRealtime'));
const AIOutputAuditPage = lazy(() => import('../pages/MaxStubs/AIOutputAudit'));
const LoginPage = lazy(() => import('../pages/Auth/Login'));
const OnboardingPage = lazy(() => import('../pages/Auth/Onboarding'));
const RegisterTenantPage = lazy(() => import('../pages/Auth/RegisterTenant'));
const CompleteRegistrationPage = lazy(() => import('../pages/Auth/CompleteRegistration'));
const ReportsIndex = lazy(() => import('../pages/Reports/Index'));
const SetupWizard = lazy(() => import('../pages/Setup/SetupWizard'));
const AcceptInvitePage = lazy(() => import('../pages/Auth/AcceptInvite'));
const SelfRegistrationPage = lazy(() => import('../pages/Org/SelfRegistration'));
const DiagnosticPage = lazy(() => import('../pages/Diagnostic/Index'));
const OfficialStatusPage = lazy(() => import('../pages/OfficialStatus/Index'));
const ChatPage = lazy(() => import('../pages/Chat/Index'));
const PlaygroundPage = lazy(() => import('../pages/Playground/Index'));
const MyApiKeysPage = lazy(() => import('../pages/My/ApiKeys'));
const MyUsagePage = lazy(() => import('../pages/My/Usage'));
const MyModelsPage = lazy(() => import('../pages/My/Models'));
const LeaderboardPage = lazy(() => import('../pages/Leaderboard/Index'));
// SaaS 门户 + 内部租户调试(main 的 SaaS 多租户基础)。
const SaasPortalLoginPage = lazy(() => import('../pages/SaaS/Login'));
const SaasHomePage = lazy(() => import('../pages/SaaS/Home'));
const TenantDebugPage = lazy(() => import('../pages/Internal/TenantDebug'));

// ── Suspense fallback ───────────────────────────────────────────────────────
const Loading = () => (
  <div className="flex items-center justify-center h-full min-h-[200px] text-gray-400">
    Loading...
  </div>
);

const Lazy = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
);

// ── Helpers ─────────────────────────────────────────────────────────────────
function LegacyCredentialRedirect() {
  const params = useParams();
  return <Navigate to={`/token-sources/${encodeURIComponent(params.id ?? '')}`} replace />;
}

const placeholder = (title: string, flag?: keyof FeatureFlags) => {
  const page = <EmptyPage title={title} description="正在开发中,即将上线" />;
  return flag ? <PermissionGate flag={flag}>{page}</PermissionGate> : page;
};

/**
 * FirstRunGate — out-of-box 首启守卫。
 *
 * 应用入口先查 `GET /setup/status`(公开端点)。后端用 `userCount === 0` 作为
 * "首启未完成"的标记(见 apps/enterprise-gateway/src/routes/setup.ts):
 *   - setupRequired === true  → 重定向到 /setup(激活 + 建初始管理员)。
 *   - setupRequired === false → 首启已完成,放行正常流程(登录 → 后台)。
 *
 * 安全降级:加载中先不渲染;接口异常时不拦截,避免把现有部署挡在门外。
 * 注:这里只覆盖"首启激活完成"这一层;"配置引导(/onboarding)完成"是另一层,
 * 需要后端 /onboarding/complete 标记后才能据此自动重定向。
 */
function FirstRunGate() {
  const location = useLocation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['setup-status'],
    queryFn: () => apiFetch<{ setupRequired: boolean; userCount: number }>('/setup/status'),
    retry: false,
    staleTime: 60_000,
  });

  if (isLoading) return null;
  if (!isError && data?.setupRequired && location.pathname !== '/setup') {
    return <Navigate to="/setup" replace />;
  }
  return <Outlet />;
}

/**
 * PostLoginLanding — 登录后的落地决策(仅作用于 `/` index)。
 *
 * 出厂/首启场景下,管理员登录后应先进入 `/onboarding` 配置引导(OFB),而非直接到
 * dashboard。判定:admin 角色 + `GET /admin/onboarding/status` 的 `completedAt === null`。
 *   - 满足 → 重定向 /onboarding(该页有「退出 → dashboard」与各配置页深链,不会困住用户)。
 *   - 其它(非 admin / 已完成 / 接口不可用返回 null)→ 正常进 /dashboard。
 * 只在 index 决策,不拦截 /dashboard 等具体页,避免新实例(尚无 token 源/钱包)无法
 * 离开引导去配置而死锁。
 */
export function PostLoginLanding() {
  const { user } = useAuth();
  const { data, isLoading } = useOnboardingStatus();
  if (isLoading) return null;
  if (user?.role === 'admin' && data?.completedAt === null) {
    return <Navigate to="/onboarding" replace />;
  }
  if (user?.role === 'member') {
    return <Navigate to="/leaderboard" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

export const registrationRoutes: RouteObject[] = [
  {
    element: <SaasEntryGate registrationRequired />,
    children: [
      {
        path: '/register',
        element: (
          <Lazy>
            <RegisterTenantPage />
          </Lazy>
        ),
      },
      {
        path: '/register/complete',
        element: (
          <Lazy>
            <CompleteRegistrationPage />
          </Lazy>
        ),
      },
      {
        path: '/complete-registration',
        element: (
          <Lazy>
            <CompleteRegistrationPage />
          </Lazy>
        ),
      },
    ],
  },
];

const routes: RouteObject[] = [
  ...registrationRoutes,
  // SaaS 门户 — 公开,独立于单租户首启守卫(FirstRunGate)之外(main 的多租户基础)。
  {
    element: <SaasEntryGate />,
    children: [
      {
        path: '/saas-login',
        element: (
          <Lazy>
            <SaasPortalLoginPage />
          </Lazy>
        ),
      },
    ],
  },
  {
    path: '/saas',
    element: <SaaSShell />,
    children: [
      {
        index: true,
        element: (
          <Lazy>
            <SaasHomePage />
          </Lazy>
        ),
      },
    ],
  },
  {
    element: <FirstRunGate />,
    children: [
      {
        path: '/login',
        element: (
          <Lazy>
            <LoginPage />
          </Lazy>
        ),
      },
      // /setup — first-run wizard, public (no auth required). Redirects to / if already configured.
      {
        path: '/setup',
        element: (
          <Lazy>
            <SetupWizard />
          </Lazy>
        ),
      },
      // Public — the invite token is the credential (see POST /auth/accept-invite).
      {
        path: '/invite/accept',
        element: (
          <Lazy>
            <AcceptInvitePage />
          </Lazy>
        ),
      },
      {
        path: '/403',
        element: <EmptyPage title="权限不足" description="此页面需要更高 license 或角色权限" />,
      },
      {
        path: '/',
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <PostLoginLanding /> },
          {
            path: 'onboarding',
            element: (
              <Lazy>
                <OnboardingPage />
              </Lazy>
            ),
          },
          {
            path: 'dashboard',
            element: (
              <RoleGate roles={['admin', 'dept_admin', 'auditor']} redirectTo="/leaderboard">
                <Lazy>
                  <DashboardPage />
                </Lazy>
              </RoleGate>
            ),
          },
          {
            path: 'leaderboard',
            element: (
              <Lazy>
                <LeaderboardPage />
              </Lazy>
            ),
          },
          {
            path: 'token-sources',
            element: (
              <Lazy>
                <CredentialsListPage />
              </Lazy>
            ),
          },
          {
            path: 'token-sources/new',
            element: (
              <Lazy>
                <CredentialNewPage />
              </Lazy>
            ),
          },
          {
            path: 'token-sources/:id',
            element: (
              <Lazy>
                <CredentialEditPage />
              </Lazy>
            ),
          },
          {
            // PR 3 Task 4.3 — OFB「为来源配置费率」深链页(骨架,表格见 4.4/4.5)。
            path: 'token-sources/:id/rate-config',
            element: (
              <Lazy>
                <RateConfigPage />
              </Lazy>
            ),
          },
          {
            path: 'token-sources/:id/showback-config',
            element: (
              <Lazy>
                <ByokShowbackConfigPage />
              </Lazy>
            ),
          },
          { path: 'credentials', element: <Navigate to="/token-sources" replace /> },
          { path: 'credentials/new', element: <Navigate to="/token-sources/new" replace /> },
          { path: 'credentials/:id', element: <LegacyCredentialRedirect /> },
          { path: 'credentials/pool', element: placeholder('凭证池调度', 'multi_user.enabled') },
          {
            path: 'routing',
            element: (
              <Lazy>
                <RoutingPage />
              </Lazy>
            ),
          },
          { path: 'routing/:policyId/edit', element: placeholder('编辑路由策略') },
          { path: 'routing/labels', element: placeholder('用途标签路由', 'multi_user.enabled') },
          { path: 'routing/providers', element: placeholder('Provider 抽象') },
          {
            path: 'model-inventory',
            element: (
              <PermissionGate flag="multi_user.enabled">
                <Lazy>
                  <ModelInventoryPage />
                </Lazy>
              </PermissionGate>
            ),
          },
          {
            // Model catalog simplification: fixed-route page retired; activation
            // auto-publishes into the model catalog. Redirect to /model-inventory.
            path: 'models',
            element: <Navigate to="/model-inventory" replace />,
          },
          {
            path: 'wallets',
            element: (
              <PermissionGate flag="wallet_sharing.enabled">
                <Lazy>
                  <WalletsPage />
                </Lazy>
              </PermissionGate>
            ),
          },
          {
            path: 'wallets/:id',
            element: (
              <PermissionGate flag="wallet_sharing.enabled">
                <Lazy>
                  <WalletDetailPage />
                </Lazy>
              </PermissionGate>
            ),
          },
          {
            path: 'usage',
            element: (
              <Lazy>
                <UsagePage />
              </Lazy>
            ),
          },
          {
            path: 'usage/attribution',
            element: (
              <PermissionGate flag="attribution.three_dim">
                <Lazy>
                  <UsageAttributionPage />
                </Lazy>
              </PermissionGate>
            ),
          },
          {
            path: 'usage/byok-costs',
            element: (
              <Lazy>
                <ByokAllocatedCostsPage />
              </Lazy>
            ),
          },
          {
            path: 'usage/cost-compare',
            element: placeholder('跨模型成本对比', 'attribution.three_dim'),
          },
          { path: 'usage/reports', element: placeholder('用量报表', 'audit.archive') },
          {
            path: 'conversations',
            element: (
              <PermissionGate flag="conversations.retention">
                <Lazy>
                  <ConversationsListPage />
                </Lazy>
              </PermissionGate>
            ),
          },
          {
            path: 'conversations/:id',
            element: (
              <PermissionGate flag="conversations.retention">
                <Lazy>
                  <ConversationDetailPage />
                </Lazy>
              </PermissionGate>
            ),
          },
          {
            path: 'conversations/classify',
            element: (
              <PermissionGate flag="conversations.classification">
                <Lazy>
                  <ClassifyPage />
                </Lazy>
              </PermissionGate>
            ),
          },
          {
            path: 'conversations/graph',
            element: (
              <PermissionGate flag="knowledge_graph.enabled">
                <Lazy>
                  <GraphPage />
                </Lazy>
              </PermissionGate>
            ),
          },
          {
            path: 'conversations/wiki',
            element: (
              <PermissionGate flag="wiki.auto_generate">
                <Lazy>
                  <WikiListPage />
                </Lazy>
              </PermissionGate>
            ),
          },
          {
            path: 'conversations/wiki/:slug',
            element: (
              <PermissionGate flag="wiki.auto_generate">
                <Lazy>
                  <WikiArticlePage />
                </Lazy>
              </PermissionGate>
            ),
          },
          {
            path: 'conversations/rag-savings',
            element: (
              <PermissionGate flag="rag.savings_panel">
                <Lazy>
                  <RAGSavingsPage />
                </Lazy>
              </PermissionGate>
            ),
          },
          {
            path: 'security/rules',
            element: (
              <PermissionGate flag="dlp.hard_block">
                <Lazy>
                  <SecurityRulesPage />
                </Lazy>
              </PermissionGate>
            ),
          },
          {
            path: 'security/rules/:id/edit',
            element: placeholder('编辑 DLP 规则', 'dlp.hard_block'),
          },
          {
            path: 'security/audit',
            element: (
              <PermissionGate flag="audit.archive">
                <Lazy>
                  <SecurityAuditPage />
                </Lazy>
              </PermissionGate>
            ),
          },
          {
            path: 'security/slm-audit',
            element: (
              <Lazy>
                <SLMAuditPage />
              </Lazy>
            ),
          },
          {
            path: 'security/pii-realtime',
            element: (
              <Lazy>
                <PIIRealtimePage />
              </Lazy>
            ),
          },
          {
            path: 'security/ai-output-audit',
            element: (
              <Lazy>
                <AIOutputAuditPage />
              </Lazy>
            ),
          },
          {
            path: 'org/members',
            element: (
              <PermissionGate flag="multi_user.enabled">
                <RoleGate roles={['admin', 'dept_admin']}>
                  <Lazy>
                    <OrgMembersPage />
                  </Lazy>
                </RoleGate>
              </PermissionGate>
            ),
          },
          {
            path: 'org/departments',
            element: (
              <PermissionGate flag="multi_user.enabled">
                <RoleGate roles={['admin', 'dept_admin']}>
                  <Lazy>
                    <OrgDepartmentsPage />
                  </Lazy>
                </RoleGate>
              </PermissionGate>
            ),
          },
          {
            path: 'org/self-registration',
            element: (
              <PermissionGate flag="multi_user.enabled">
                <RoleGate roles={['admin']}>
                  <Lazy>
                    <SelfRegistrationPage />
                  </Lazy>
                </RoleGate>
              </PermissionGate>
            ),
          },
          {
            path: 'org/sso',
            element: (
              <PermissionGate flag="sso.full">
                <RoleGate roles={['admin']}>
                  <Lazy>
                    <SSOConfigPage />
                  </Lazy>
                </RoleGate>
              </PermissionGate>
            ),
          },
          {
            path: 'integrations/directory',
            element: (
              <PermissionGate flag="directory.sync">
                <RoleGate roles={['admin']}>
                  <Lazy>
                    <DirectoryIntegrationPage />
                  </Lazy>
                </RoleGate>
              </PermissionGate>
            ),
          },
          {
            path: 'org/approval-integrations',
            element: placeholder('飞书 / 钉钉审批', 'rbac.custom_roles'),
          },
          {
            path: 'reports',
            element: (
              <Lazy>
                <ReportsIndex />
              </Lazy>
            ),
          },
          { path: 'reports/builder', element: placeholder('报表构建器', 'audit.archive') },
          { path: 'settings/license', element: placeholder('License 信息') },
          {
            path: 'settings/api-keys',
            element: (
              <Lazy>
                <ApiKeysPage />
              </Lazy>
            ),
          },
          {
            path: 'settings/dlp-rules',
            element: (
              <Lazy>
                <DlpRulesPage />
              </Lazy>
            ),
          },
          {
            path: 'settings/oidc-providers',
            element: (
              <Lazy>
                <OidcProvidersPage />
              </Lazy>
            ),
          },
          {
            path: 'settings/org',
            element: (
              <Lazy>
                <OrgSettingsPage />
              </Lazy>
            ),
          },
          { path: 'settings/general', element: placeholder('系统设置') },
          {
            path: 'settings/about',
            element: (
              <Lazy>
                <AboutPage />
              </Lazy>
            ),
          },
          {
            path: 'diagnostic',
            element: (
              <Lazy>
                <DiagnosticPage />
              </Lazy>
            ),
          },
          {
            path: 'official-status',
            element: (
              <Lazy>
                <OfficialStatusPage />
              </Lazy>
            ),
          },
          {
            path: 'chat',
            element: (
              <Lazy>
                <ChatPage />
              </Lazy>
            ),
          },
          {
            path: 'playground',
            element: (
              <Lazy>
                <PlaygroundPage />
              </Lazy>
            ),
          },
          {
            // 内部租户调试(main 的 SaaS 多租户基础);后端 /admin/internal/tenant-debug-session 自鉴权。
            path: 'internal/tenant-debug',
            element: (
              <Lazy>
                <TenantDebugPage />
              </Lazy>
            ),
          },
          {
            path: 'my/api-keys',
            element: (
              <Lazy>
                <MyApiKeysPage />
              </Lazy>
            ),
          },
          {
            path: 'my/usage',
            element: (
              <Lazy>
                <MyUsagePage />
              </Lazy>
            ),
          },
          {
            path: 'my/models',
            element: (
              <Lazy>
                <MyModelsPage />
              </Lazy>
            ),
          },
        ],
      },
      { path: '*', element: <EmptyPage title="404" description="找不到此页面" /> },
    ],
  },
];

export const router = createBrowserRouter(routes, {
  basename: normalizeAdminWebBasePath(import.meta.env['VITE_ADMIN_WEB_BASE_PATH']),
});
